import { Directive, OnInit, Input, TemplateRef, ViewContainerRef, SimpleChanges, OnChanges, Component, ElementRef, Renderer2, ViewChild } from '@angular/core';
import { JwtLegFiClaims } from '../../../../services/auth/jwt-legfi-claims.model';
import { LegFiJwtService } from '../../../../services/auth/legfi-jwt.service';
import { MatIcon, MatIconModule } from '@angular/material/icon';

/** Standalone directive to check against token for module permission */
@Directive({
    selector: '[hasPermission]',
    standalone: true,
})
export class HasPermissionDirective implements OnChanges
{
    @Input('hasPermission') moduleName: string | undefined;
    @Input('hasPermissionRequiresWrite') requiresWrite: boolean | undefined;

    constructor(
            private _vcr: ViewContainerRef,
            private _tpl: TemplateRef<any>,
    ){
    }

    ngOnChanges({ moduleName, requiresWrite }: SimpleChanges) {
        this._vcr.clear();

        if (LegFiJwtService.doesUserHaveModulePermission(moduleName.currentValue, requiresWrite.currentValue)) {
            this._vcr.createEmbeddedView(this._tpl);
        }
    }
}

/** Standalone directive to check against token for membership access */
@Directive({
    selector: '[memberAccess]',
    standalone: true,
})
export class MemberAccessDirective implements OnInit
{
    private _jwt: JwtLegFiClaims;
    private _hasAccess: boolean;

    constructor(
            private _vcr: ViewContainerRef,
            private _tpl: TemplateRef<any>,
    ){
        this._jwt = LegFiJwtService.read();
    }

    @Input() set memberAccess(condition: boolean) {
        this.updateViewContainer(condition);
    }

    ngOnInit() {
        if (this._hasAccess === undefined) {
            this.updateViewContainer(!!this._jwt.memberId);
        }
    }

    private updateViewContainer(condition: boolean) {
        this._vcr.clear();

        this._hasAccess = condition;
        if (this._hasAccess) {
            this._vcr.createEmbeddedView(this._tpl);
        }
    }
}

/** Standalone directive to check against token for admin access */
@Directive({
    selector: '[adminAccess]',
    standalone: true,
})
export class AdminAccessDirective implements OnChanges, OnInit
{
    @Input('adminAccess') hasAdminAccess: boolean | undefined;
    @Input() includeSuper: boolean | undefined;

    private _jwt: JwtLegFiClaims;
    private _hasAccess: boolean;

    constructor(
            private _vcr: ViewContainerRef,
            private _tpl: TemplateRef<any>,
    ) {
        this._jwt = LegFiJwtService.read();
    }

    ngOnChanges({ hasAdminAccess, includeSuper }: SimpleChanges) {
        this._hasAccess = includeSuper.currentValue ? !!(hasAdminAccess.currentValue || this._jwt.superUser) : !!hasAdminAccess.currentValue;
        this.updateViewContainer();
    }

    ngOnInit() {
        if (this._hasAccess === undefined) {
            this._hasAccess = this.includeSuper ? !!(this._jwt.admin || this._jwt.superUser) : !!this._jwt.admin;
            this.updateViewContainer();
        }
    }

    private updateViewContainer() {
        this._vcr.clear();

        if (this._hasAccess) {
            this._vcr.createEmbeddedView(this._tpl);
        }
    }
}

/** Standalone component to inject superstar icon next to super action */
@Component({
    selector: 'app-superstar',
    template: `<mat-icon class="app-superstar">star_outline</mat-icon>`,
    standalone: true,
    imports: [
        MatIconModule,
    ],
})
export class SuperstarComponent
{
    @ViewChild(MatIcon, { read: ElementRef, static: true }) icon: ElementRef;
}

/** Standalone directive to check against token for super access */
@Directive({
    selector: '[superAccess]',
    standalone: true,
})
export class SuperAccessDirective implements OnChanges, OnInit
{
    @Input('superAccess') hasSuperAccess: boolean | undefined;
    @Input() showStar: boolean | undefined;

    private _jwt: JwtLegFiClaims;
    private _hasAccess: boolean;

    constructor(
            private _vcr: ViewContainerRef,
            private _tpl: TemplateRef<any>,
            private _renderer: Renderer2,
    ){
        this._jwt = LegFiJwtService.read();
    }

    ngOnChanges({ hasSuperAccess, showStar }: SimpleChanges) {
        this._hasAccess = !!hasSuperAccess.currentValue;
        this.updateViewContainer(showStar.currentValue);
    }

    ngOnInit() {
        if (this._hasAccess === undefined) {
            this._hasAccess = !!this._jwt.superUser;
            this.updateViewContainer(this.showStar ?? true);
        }
    }

    private updateViewContainer(showSuperStar: boolean) {
        this._vcr.clear();

        if (this._hasAccess) {
            const embedded = this._vcr.createEmbeddedView(this._tpl);
            if (showSuperStar) {
                this.appendStarIcon(embedded.rootNodes.at(0));
            }
        }
    }

    private appendStarIcon(node: any) {
        const componentRef = this._vcr.createComponent(SuperstarComponent);
        let parentRef = node;

        node.childNodes.forEach((child: any) => {
            if (child?.classList?.contains('mdc-button__label') || child?.classList?.contains('mdc-list-item__primary-text')) {
                parentRef = child;
            }
        });

        parentRef.classList.add('super-action');
        this._renderer.appendChild(parentRef, componentRef.instance.icon.nativeElement);
    }
}
